"use client";

/**
 * / — Lista top-level de Levantamentos (TIDE VÍDEO standalone, #391 Parte A).
 *
 * App standalone (ADR-VID-005): não existe conceito de projeto — todo
 * levantamento é criado e listado direto na raiz, sem vínculo.
 *
 * Pattern espelhado de /rodovias (top-level + header próprio) do TIDE APP.
 *
 * #300 — Botão "Novo Levantamento" abre diálogo com:
 *   - campo âncora: "Início da gravação"
 *   Janelas de análise foram movidas para a tela de conferência (#300 Part 2).
 *
 * #300 — âncora enviada como wall-clock raw (sem toISOString); display usa
 *   formatDateTime (string-slice, não new Date → sem shift de fuso).
 *
 * #301 — dialog ganha km, latitude, longitude (opcionais; espelha backend).
 *
 * #477 — criação ganha upload multi-arquivo NA MESMA TELA (mesmo Dialog):
 *   fase "form" (dados do posto) -> ao criar, o MESMO diálogo troca pra fase
 *   "upload" (fila sequencial client-side, #477 achado 2) -> "Processar" é
 *   um botão explícito separado (D2 — sem auto-dispatch). Consolida os 2
 *   caminhos duplicados do achado 1 (dialog "+ Novo Levantamento" vs página
 *   de upload standalone) num fluxo só.
 */

import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import { Loader2Icon, PlusIcon } from "lucide-react";
import { toast } from "sonner";

import { SortableTable } from "@/components/tables/sortable-table";
import { DeleteLevantamentoButton } from "./delete-levantamento-button";
import { RetentionBadge } from "@/components/video/retention-badge";
import { VideoInboxHint } from "@/components/video/video-inbox-hint";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCreateLevantamento,
  useInboxVideos,
  useLevantamentos,
  useProcessLevantamento,
  videoUploadPageUrl,
} from "@/lib/api-video";
import type { LevantamentoCreate } from "@/lib/api-video";
import { filaStatusColorCls, filaStatusShortLabel } from "@/lib/fila-helpers";
import { formatDate, formatDateTime } from "@/lib/formatters";
import { uploadVideoFile } from "@/lib/upload-client";
import type { LevantamentoOut, LevantamentoStatus } from "@/types/video";

// ---------------------------------------------------------------------------
// Create levantamento dialog (#237 / #301)
// ---------------------------------------------------------------------------

const SENTIDO_OPTIONS = ["NS", "SN", "LO", "OL", "Único"] as const;

// ---------------------------------------------------------------------------
// #477 — fila de upload sequencial no cliente (mesma tela da criação)
// ---------------------------------------------------------------------------

type UploadItemStatus = "pending" | "uploading" | "done" | "error";

interface UploadQueueItem {
  id: string;
  file: File;
  status: UploadItemStatus;
  progress: number;
  error?: string;
  videoId?: string;
}

/** Fila sequencial (#477 achado 2: "fila sequencial no cliente, infra
 * chunked já existe") — um arquivo de cada vez, na ordem em que foram
 * selecionados. `updateItem` é a única superfície de mutação de estado
 * (vem do componente via useCallback), então esta função fica pura o
 * bastante pra não precisar de mock de React em teste. */
async function uploadQueueSequential(
  items: UploadQueueItem[],
  levId: string,
  updateItem: (id: string, patch: Partial<UploadQueueItem>) => void,
): Promise<void> {
  for (const item of items) {
    updateItem(item.id, { status: "uploading", progress: 0, error: undefined });
    try {
      const result = await uploadVideoFile(item.file, levId, {
        onProgress: (pct) => updateItem(item.id, { progress: pct }),
      });
      updateItem(item.id, {
        status: "done",
        progress: 100,
        videoId: result.video_id,
      });
    } catch (e) {
      updateItem(item.id, {
        status: "error",
        error: (e as Error)?.message ?? "Falha no upload.",
      });
    }
  }
}

interface CreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateLevantamentoDialog({
  open,
  onOpenChange,
}: CreateDialogProps) {
  const router = useRouter();
  const createMut = useCreateLevantamento();

  // #477 — fase do diálogo: "form" (dados do posto) -> "upload" (mesma tela,
  // sem navegar/fechar) -> Processar explícito (D2, sem auto-dispatch).
  const [phase, setPhase] = useState<"form" | "upload">("form");
  const [createdLev, setCreatedLev] = useState<LevantamentoOut | null>(null);
  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const processMut = useProcessLevantamento(createdLev?.id ?? "");

  // Core fields
  const [posto, setPosto] = useState("");
  const [rodovia, setRodovia] = useState("");
  const [sentido, setSentido] = useState("NS");
  const [movimento, setMovimento] = useState("");
  // #301 — geolocation fields (optional)
  const [km, setKm] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  // Âncora — "Início da gravação"
  const [dataHoraInicial, setDataHoraInicial] = useState("");

  // Error state
  const [formError, setFormError] = useState<string | null>(null);

  function resetForm() {
    setPosto("");
    setRodovia("");
    setSentido("NS");
    setMovimento("");
    setKm("");
    setLatitude("");
    setLongitude("");
    setDataHoraInicial("");
    setFormError(null);
  }

  // #477 — reset completo (form + fase + fila) ao fechar o diálogo, pra uma
  // próxima abertura sempre começar do zero na fase "form".
  function resetAll() {
    resetForm();
    setPhase("form");
    setCreatedLev(null);
    setQueue([]);
  }

  function handleDialogOpenChange(next: boolean) {
    if (!next) resetAll();
    onOpenChange(next);
  }

  const updateQueueItem = useCallback(
    (id: string, patch: Partial<UploadQueueItem>) => {
      setQueue((prev) =>
        prev.map((it) => (it.id === id ? { ...it, ...patch } : it)),
      );
    },
    [],
  );

  // #477 — seleção de arquivos: <input multiple> + fila sequencial client-
  // side (achado 2). Cada vídeo já sai ASSOCIADO ao levantamento no
  // POST /upload/{id}/complete (achado 3: zero passo manual de "Anexar").
  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    if (!createdLev) return;
    const files = Array.from(e.target.files ?? []);
    e.target.value = ""; // permite re-selecionar o mesmo arquivo depois
    if (files.length === 0) return;

    const newItems: UploadQueueItem[] = files.map((file, idx) => ({
      id: `${Date.now()}-${idx}-${file.name}`,
      file,
      status: "pending",
      progress: 0,
    }));
    setQueue((prev) => [...prev, ...newItems]);
    void uploadQueueSequential(newItems, createdLev.id, updateQueueItem);
  }

  function handleRetryItem(item: UploadQueueItem) {
    if (!createdLev) return;
    void uploadQueueSequential([item], createdLev.id, updateQueueItem);
  }

  const anyUploaded = queue.some((q) => q.status === "done");
  const isUploading = queue.some((q) => q.status === "uploading");

  // #477 D2 — "Processar" é o único botão explícito que dispara a fila
  // (backend NUNCA recebe process:true deste fluxo — ver upload-client.ts).
  function handleProcess() {
    if (!createdLev) return;
    processMut.mutate(undefined, {
      onSuccess: (result) => {
        toast.success(
          `Processamento iniciado. ${result.videos_queued} vídeo(s) na fila.`,
        );
        onOpenChange(false);
        resetAll();
        router.push(`/${createdLev.id}`);
      },
      onError: () =>
        toast.error(
          "Falha ao iniciar processamento. Verifique se há vídeos enviados.",
        ),
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    // Validate required fields
    if (!posto.trim()) {
      setFormError("Posto de contagem é obrigatório.");
      return;
    }
    if (!dataHoraInicial) {
      setFormError("Início da gravação é obrigatório.");
      return;
    }

    // #331 — lat/lng are now required
    if (!latitude.trim()) {
      setFormError("Latitude é obrigatória.");
      return;
    }
    if (!longitude.trim()) {
      setFormError("Longitude é obrigatória.");
      return;
    }

    // #301 — validate numeric fields
    const latNum = latitude.trim() ? Number(latitude.trim()) : null;
    const lngNum = longitude.trim() ? Number(longitude.trim()) : null;
    if (latNum !== null && (isNaN(latNum) || latNum < -90 || latNum > 90)) {
      setFormError("Latitude deve ser um número entre -90 e 90.");
      return;
    }
    if (lngNum !== null && (isNaN(lngNum) || lngNum < -180 || lngNum > 180)) {
      setFormError("Longitude deve ser um número entre -180 e 180.");
      return;
    }

    const payload: LevantamentoCreate = {
      posto_contagem: posto.trim(),
      rodovia: rodovia.trim() || null,
      km: km.trim() || null,
      latitude: latNum !== null ? String(latNum) : null,
      longitude: lngNum !== null ? String(lngNum) : null,
      sentido,
      movimento: movimento.trim() || null,
      // #300 — send raw wall-clock digits; backend Pydantic validator does
      // .replace(tzinfo=utc) so the digits arrive unchanged, no tz shift.
      // datetime-local yields "YYYY-MM-DDTHH:MM" (len 16); append ":00" for
      // seconds so backend datetime parser accepts it unambiguously.
      data_hora_inicial:
        dataHoraInicial.length === 16
          ? dataHoraInicial + ":00"
          : dataHoraInicial,
      // #300 Part 2 — janelas moved to the conference screen; levantamento
      // always starts without windows (first count = full block, legado).
      janelas_analise: null,
    };

    createMut.mutate(payload, {
      // #477 D1 — NÃO navega/fecha: o MESMO diálogo troca pra fase "upload"
      // (criação + upload multi-arquivo na mesma tela, achado 1).
      onSuccess: (lev) => {
        resetForm();
        setCreatedLev(lev);
        setPhase("upload");
      },
      onError: (err) => {
        setFormError(
          (err as Error)?.message ?? "Erro ao criar levantamento.",
        );
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {phase === "form" ? "Novo Levantamento" : "Enviar vídeos"}
          </DialogTitle>
        </DialogHeader>

        {phase === "form" && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-sm">
          {/* Posto */}
          <FormField label="Posto de contagem *">
            <input
              className={inputCls}
              value={posto}
              onChange={(e) => setPosto(e.target.value)}
              placeholder="ex. BR-381 km 142 Norte"
              required
            />
          </FormField>

          {/* Rodovia */}
          <FormField label="Rodovia">
            <input
              className={inputCls}
              value={rodovia}
              onChange={(e) => setRodovia(e.target.value)}
              placeholder="ex. BR-381"
            />
          </FormField>

          {/* Sentido */}
          <FormField label="Sentido *">
            <select
              className={inputCls}
              value={sentido}
              onChange={(e) => setSentido(e.target.value)}
            >
              {SENTIDO_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>

          {/* Movimento */}
          <FormField label="Movimento">
            <input
              className={inputCls}
              value={movimento}
              onChange={(e) => setMovimento(e.target.value)}
              placeholder="ex. TMC"
            />
          </FormField>

          {/* #301 — Geolocalização: km, latitude, longitude (opcionais) */}
          <FormField
            label="KM"
            help="Quilômetro do posto na rodovia (ex. 142.5). Opcional, mas recomendado para identificação no CSV."
          >
            <input
              type="number"
              step="0.001"
              className={inputCls}
              value={km}
              onChange={(e) => setKm(e.target.value)}
              placeholder="ex. 142.5"
              data-testid="field-km"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Latitude *">
              <input
                type="number"
                step="0.000001"
                min={-90}
                max={90}
                className={inputCls}
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="-19.912"
                data-testid="field-latitude"
                required
              />
            </FormField>
            <FormField label="Longitude *">
              <input
                type="number"
                step="0.000001"
                min={-180}
                max={180}
                className={inputCls}
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-43.940"
                data-testid="field-longitude"
                required
              />
            </FormField>
          </div>

          {/* Âncora — SPEC D3 (#300) */}
          <FormField
            label="Início da gravação *"
            help="Horário em que a gravação começou (do seu registro/equipamento). As faixas de horário do levantamento você escolhe no próximo passo, depois de processar."
          >
            <input
              type="datetime-local"
              className={inputCls}
              value={dataHoraInicial}
              onChange={(e) => setDataHoraInicial(e.target.value)}
              required
            />
          </FormField>

          {formError && (
            <p className="text-xs text-red-400" role="alert">
              {formError}
            </p>
          )}
        </form>
        )}

        {/* #477 D1/D2 — fase "upload": mesma tela, mesmo diálogo. Operador
            seleciona N arquivos (fila sequencial client-side), confere a
            lista e clica "Processar" explicitamente (sem auto-dispatch). */}
        {phase === "upload" && createdLev && (
          <div className="flex flex-col gap-4 text-sm">
            <p className="text-xs text-tide-text-secondary">
              Levantamento <strong>{createdLev.posto_contagem}</strong>{" "}
              criado. Envie os vídeos deste posto abaixo.
            </p>

            <FormField label="Vídeos (selecione um ou mais arquivos)">
              <input
                type="file"
                accept="video/*,.mp4,.mov,.m4v"
                multiple
                onChange={handleFilesSelected}
                data-testid="input-upload-files"
                className={inputCls}
              />
            </FormField>

            {queue.length > 0 && (
              <ul
                className="flex flex-col gap-2"
                data-testid="upload-queue-list"
              >
                {queue.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-2 rounded border border-tide-border px-2 py-1.5 text-xs"
                    data-testid={`upload-item-${item.id}`}
                  >
                    <span className="truncate">{item.file.name}</span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      {item.status === "pending" && (
                        <span className="text-tide-text-muted">
                          Aguardando…
                        </span>
                      )}
                      {item.status === "uploading" && (
                        <span className="flex items-center gap-1 text-tide-accent">
                          <Loader2Icon className="size-3 animate-spin" />
                          {item.progress}%
                        </span>
                      )}
                      {item.status === "done" && (
                        <span
                          className="text-green-600 dark:text-green-400"
                          data-testid={`upload-item-done-${item.id}`}
                        >
                          Enviado ✓
                        </span>
                      )}
                      {item.status === "error" && (
                        <>
                          <span
                            className="text-red-500 dark:text-red-400"
                            title={item.error}
                          >
                            Falhou
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleRetryItem(item)}
                            data-testid={`btn-retry-${item.id}`}
                          >
                            Tentar de novo
                          </Button>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {!anyUploaded && (
              <p className="text-xs text-tide-text-muted">
                Envie ao menos 1 vídeo pra habilitar o processamento.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === "form" ? (
            <Button
              type="submit"
              disabled={createMut.isPending}
              onClick={(e) => {
                // delegate to the form submit handler via a synthetic event on the
                // wrapping form — simpler than ref forwarding
                const form = (e.currentTarget as HTMLElement)
                  .closest("[data-slot='dialog-content']")
                  ?.querySelector("form");
                form?.requestSubmit();
              }}
            >
              {createMut.isPending ? "Criando…" : "Criar levantamento"}
            </Button>
          ) : (
            <Button
              onClick={handleProcess}
              disabled={!anyUploaded || isUploading || processMut.isPending}
              data-testid="btn-processar-inline"
            >
              {processMut.isPending ? "Iniciando…" : "Processar"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type StatusFilter = LevantamentoStatus | "todos";

export default function VideoListPage() {
  const router = useRouter();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("todos");
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: levantamentos = [], isLoading } = useLevantamentos(
    statusFilter === "todos" ? {} : { status: statusFilter },
  );

  // #299 — indicador discreto de vídeos não atribuídos na inbox
  const { data: inboxVideos } = useInboxVideos();
  const unassignedCount = inboxVideos?.length ?? 0;

  return (
    <>
      <div className="flex items-center justify-between border-b border-tide-border bg-tide-bg-secondary px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold text-tide-text-primary">
            Vídeo — Levantamentos
          </h1>
          <p className="mt-0.5 text-xs text-tide-text-secondary">
            Pipeline ML de detecção e contagem por câmera.
          </p>
          {/* #299 — indicador discreto de vídeos não atribuídos na inbox */}
          {unassignedCount > 0 && (
            <p
              className="mt-1 text-xs text-amber-600 dark:text-amber-400"
              data-testid="inbox-unassigned-badge"
            >
              {unassignedCount}{" "}
              {unassignedCount === 1
                ? "vídeo não atribuído"
                : "vídeos não atribuídos"}{" "}
              na inbox
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* #391 Parte A.3 — link pra página de upload retomável servida na
              raiz do host da API (fora de /api/v1), ver videoUploadPageUrl. */}
          <a
            href={videoUploadPageUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-tide-accent hover:underline"
            data-testid="link-upload-page"
          >
            Página de upload
          </a>
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <PlusIcon className="size-3.5" />
            Novo Levantamento
          </Button>
        </div>
      </div>

      <CreateLevantamentoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      <div className="flex-1 overflow-y-auto p-6">
        <div className="mb-4">
          <VideoInboxHint />
        </div>

        <section className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <FilterField label="Status">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className={inputCls}
            >
              <option value="todos">Todos</option>
              <option value="criado">Criado</option>
              <option value="em_processamento">Em processamento</option>
              <option value="processado">Processado</option>
              <option value="erro">Erro</option>
            </select>
          </FilterField>
        </section>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-tide-text-muted">
            <span className="material-symbols-outlined animate-spin text-sm">
              progress_activity
            </span>
            Carregando...
          </div>
        ) : levantamentos.length === 0 ? (
          <EmptyState onNew={() => setDialogOpen(true)} />
        ) : (
          <div className="flex flex-col gap-2 text-xs text-tide-text-muted">
            <div>
              {levantamentos.length}{" "}
              {levantamentos.length === 1 ? "levantamento" : "levantamentos"}
            </div>
            <SortableTable<LevantamentoOut>
              data={levantamentos}
              getRowId={(l) => l.id}
              defaultSort={{ key: "created_at", dir: "desc" }}
              urlParamPrefix="video"
              onRowClick={(l) => router.push(`/${l.id}`)}
              columns={[
                {
                  key: "posto_contagem",
                  header: "Posto / Local",
                  sort: "text",
                  format: (v, l) => (
                    <div className="flex flex-col gap-0.5">
                      <span className="font-semibold text-tide-text-primary">
                        {String(v)}
                      </span>
                      <span className="text-[11px] text-tide-text-muted">
                        {l.rodovia ?? "—"} · sentido {l.sentido}
                      </span>
                      {/* #526 — aviso discreto de retenção do bruto; null
                          (sem badge) quando bruto_expira_em ainda não se
                          aplica (levantamento nunca chegou em
                          'pronto_para_revisao'). */}
                      <RetentionBadge brutoExpiraEm={l.bruto_expira_em} />
                    </div>
                  ),
                },
                {
                  key: "data_hora_inicial",
                  header: "Início da gravação",
                  sort: "date",
                  // #300 — formatDateTime uses string-slice (not new Date), so
                  // UTC digits display as-is regardless of browser timezone.
                  format: (v) => formatDateTime(v as string),
                },
                {
                  key: "status",
                  header: "Status",
                  sort: "text",
                  format: (v) => <StatusPill status={v as LevantamentoStatus} />,
                },
                {
                  // #426 gesto 3 — placar da fila visível na lista (badge
                  // compacto; detalhe completo — posição FIFO, etapa, erro —
                  // fica no card da página do levantamento).
                  key: "fila_status",
                  header: "Fila",
                  sort: "text",
                  format: (v) => <FilaStatusPill status={v as LevantamentoOut["fila_status"]} />,
                },
                {
                  key: "created_at",
                  header: "Criado",
                  sort: "date",
                  format: (v) => formatDate(v as string),
                },
                {
                  // #548 — ação de exclusão (soft-delete) por linha.
                  key: "actions",
                  header: "",
                  sort: false,
                  align: "right",
                  format: (_v, l) => (
                    <DeleteLevantamentoButton levantamento={l} />
                  ),
                },
              ]}
            />
          </div>
        )}
      </div>
    </>
  );
}

const inputCls =
  "rounded-md border border-tide-border bg-tide-bg-hover px-2.5 py-1.5 text-sm text-tide-text-primary outline-none focus:border-tide-accent";

function FormField({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-tide-text-secondary">
        {label}
      </label>
      {help && (
        <p className="text-[11px] text-tide-text-muted">{help}</p>
      )}
      {children}
    </div>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-tide-text-secondary">
        {label}
      </label>
      {children}
    </div>
  );
}

function StatusPill({ status }: { status: LevantamentoStatus }) {
  const styles: Record<LevantamentoStatus, string> = {
    criado: "bg-tide-text-muted/20 text-tide-text-muted",
    em_processamento: "bg-yellow-500/15 text-yellow-400",
    processado: "bg-green-500/15 text-green-400",
    validated: "bg-blue-500/15 text-blue-400",
    erro: "bg-red-500/15 text-red-400",
  };
  const labels: Record<LevantamentoStatus, string> = {
    criado: "Criado",
    em_processamento: "Processando",
    processado: "Processado",
    validated: "Validado",
    erro: "Erro",
  };
  return (
    <span
      className={`rounded px-2 py-0.5 text-[10px] font-semibold ${styles[status]}`}
    >
      {labels[status]}
    </span>
  );
}

function FilaStatusPill({ status }: { status: LevantamentoOut["fila_status"] }) {
  return (
    <span
      className={`rounded px-2 py-0.5 text-[10px] font-semibold ${filaStatusColorCls(status)}`}
    >
      {filaStatusShortLabel(status)}
    </span>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div
      data-testid="video-empty-state"
      className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-tide-border bg-tide-bg-secondary py-12 px-6 text-center"
    >
      <span className="material-symbols-outlined text-5xl opacity-30">
        videocam
      </span>
      <div>
        <p className="text-sm text-tide-text-primary">
          Nenhum levantamento encontrado
        </p>
        <p className="mt-1 text-xs text-tide-text-muted">
          Solte um arquivo na pasta inbox abaixo — o worker detecta a cada ~10s.
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={onNew}>
        <PlusIcon className="size-3.5" />
        Criar manualmente
      </Button>
      <div className="w-full max-w-2xl text-left">
        <VideoInboxHint />
      </div>
    </div>
  );
}
